// components/entities/BrandRenderer.tsx
import { FaterStampIcon } from '@/components/ui/FaterStampIcon';
import React, { memo, useCallback, useMemo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import {
  Globe, MapPin,
  Fingerprint, DollarSign, Activity,
  CircleHelp, Crown, Star, Store,
  Edit, FileText, Trash2, XCircle
} from '@/components/ui/lucide-memo';
import {
  BaseEntityProps, EntityRowAction, LeasingBrandDTO,
} from '#types/types';
import clsx from 'clsx';
import { BrandMark } from '@/components/ui/BrandMark';
import { AppTooltip } from '@/components/ui/tooltip';
import { formatCnccSector } from '#lib/leasing/enrichNote';
import {
  formatSentenceCase,
  normalizeIsoAlpha2CountryCode,
  toCountryFlag,
} from '#lib/leasing/brandDisplay';
import { portalTableRow } from '../portal/portalTheme';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import { toast } from 'sonner';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';

const MemoizedGlobe = memo(Globe);
const MemoizedMapPin = memo(MapPin);
const MemoizedActivity = memo(Activity);
const MemoizedFingerprint = memo(Fingerprint);
const MemoizedDollarSign = memo(DollarSign);
const MemoizedCircleHelp = memo(CircleHelp);
const MemoizedStore = memo(Store);
const MemoizedStar = memo(Star);
const MemoizedCrown = memo(Crown);

// --- Configuration & Helpers ---

const PANEL_FONT = 'ui-sans-serif, -apple-system, system-ui, sans-serif';
const PANEL_STYLE: React.CSSProperties = { fontFamily: PANEL_FONT };
const DEFAULT_ROW_COLUMN_ORDER = ['brand', 'data', 'typology', 'positioning', 'surface', 'network', 'spacing', 'actions'] as const;
const ROW_TEXT: React.CSSProperties = { fontSize: 13, lineHeight: '18px' };
const NAME_TEXT: React.CSSProperties = { fontSize: 14, lineHeight: '20px' };
const ROW_VALUE = 'text-[#ECECEC]';
const ROW_MUTED = 'text-[#8E8E8E]';
const DEFAULT_ROW_COLUMNS: TableColumnDef[] = DEFAULT_ROW_COLUMN_ORDER.map((id) => ({ id, header: '', minWidth: 1 }));
const EMPTY_ROW_ACTIONS: EntityRowAction<LeasingBrandDTO>[] = [];

// Three frozen objects instead of a literal per cell per render: the row is drawn once per brand
// in tables that hold hundreds of them.
const CELL_STYLE_START: React.CSSProperties = { justifyContent: 'flex-start' };
const CELL_STYLE_CENTER: React.CSSProperties = { justifyContent: 'center' };
const CELL_STYLE_END: React.CSSProperties = { justifyContent: 'flex-end' };

const resolveCellStyle = (column: TableColumnDef): React.CSSProperties => {
  if (column.id === 'actions' || column.align === 'right') return CELL_STYLE_END;
  if (column.align === 'center') return CELL_STYLE_CENTER;
  return CELL_STYLE_START;
};

const translations = {
  en: {
    surface: 'Surface',
    formats: 'Formats',
    website: 'Website',
    legalEntities: 'Legal entities',
    noEntitiesLinked: 'No entities linked.',
    healthScore: 'Health Score',
    shareCapital: 'Share Capital',
    workforce: 'Workforce',
    footprint: 'Footprint',
    mapView: 'Map View',
    statsView: 'Stats View',
    opened: 'Opened',
    closed: 'Closed',
    locs: 'Locations',
    unknown: 'Unknown',
    noLocations: 'No locations',
    scanning: 'Scanning...',
    select: 'Select',
    cancel: 'Cancel',
    scan: 'Scan',
    lastScan: (val: string) => `Last scan: ${val}`,
    copyAddress: 'Copy Address',
    openInMaps: 'Open in Google Maps',
    editName: 'Edit Name',
    editDescription: 'Edit Description',
    editWebsite: 'Edit Website',
    editSiren: 'Edit SIREN',
    addFaterStamp: 'Add Fater stamp',
    removeFaterStamp: 'Remove Fater stamp',
    stampAdded: 'Fater stamp added',
    stampRemoved: 'Fater stamp removed',
    deleteLogo: 'Delete Logo',
    deleteBrand: 'Delete Brand',
    vacant: 'Vacant',
    view: 'View',
    close: 'Close',
    selectBrand: 'Select Brand',
    uploadingLogo: (name: string) => `Uploading logo for ${name}...`,
    enterBrandName: 'Enter brand name:',
    enterBrandDescription: 'Enter brand description:',
    enterWebsite: 'Enter website URL:',
    enterSiren: 'Enter SIREN:',
    deleteConfirm: 'Are you sure you want to delete this brand?',
    showLinkedLegalEntities: 'Show linked legal entities',
  },
  fr: {
    surface: 'Surface',
    formats: 'Formats',
    website: 'Site',
    legalEntities: 'Entites legales',
    noEntitiesLinked: 'Aucune entite liee.',
    healthScore: 'Score sante',
    shareCapital: 'Capital social',
    workforce: 'Effectif',
    footprint: 'Empreinte',
    mapView: 'Vue carte',
    statsView: 'Vue stats',
    opened: 'Ouvertures',
    closed: 'Fermetures',
    locs: 'Localisations',
    unknown: 'Inconnu',
    noLocations: 'Aucun lieu',
    scanning: 'Scan...',
    select: 'Selectionner',
    cancel: 'Annuler',
    scan: 'Scanner',
    lastScan: (val: string) => `Dernier scan : ${val}`,
    copyAddress: 'Copier adresse',
    openInMaps: 'Ouvrir Google Maps',
    editName: 'Modifier nom',
    editDescription: 'Modifier description',
    editWebsite: 'Modifier site',
    editSiren: 'Modifier SIREN',
    addFaterStamp: 'Ajouter le tampon Fater',
    removeFaterStamp: 'Retirer le tampon Fater',
    stampAdded: 'Tampon Fater ajouté',
    stampRemoved: 'Tampon Fater retiré',
    deleteLogo: 'Supprimer logo',
    deleteBrand: 'Supprimer marque',
    vacant: 'Vacant',
    view: 'Voir',
    close: 'Fermer',
    selectBrand: 'Selectionner',
    uploadingLogo: (name: string) => `Envoi du logo pour ${name}...`,
    enterBrandName: 'Saisir nom de marque:',
    enterBrandDescription: 'Saisir description marque:',
    enterWebsite: 'Saisir URL du site:',
    enterSiren: 'Saisir SIREN:',
    deleteConfirm: 'Supprimer cette marque ?',
    showLinkedLegalEntities: 'Voir entites legales liees',
  }
};

// --- Main Renderer ---
export interface BrandRendererProps extends BaseEntityProps<LeasingBrandDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<LeasingBrandDTO>[];
}

// What keeps a privileged brand out of the ready folder (faterStamp dataGaps), [en, fr].
const GAP_LABEL: Record<string, [string, string]> = {
  identity: ['identity', 'identité'], criteria: ['criteria', 'critères'], financials: ['financials', 'finances'],
  stores_unverified: ['store checks', 'magasins à vérifier'], stores_off_map: ['store map', 'carte des magasins'],
  verification: ['verified evidence', 'preuves vérifiées'], contacts: ['decision-maker contacts', 'contacts décisionnaires'],
  contact_photos: ['contact photos', 'photos des contacts'],
};

export const BrandRenderer = memo(({
  entityId,
  variant = 'card',
  onClick,
  className,
  columns,
  rowActions = EMPTY_ROW_ACTIONS,
}: BrandRendererProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];

  const data = useKernelStore(state => state.inventory.Brand.registry[entityId]) as LeasingBrandDTO | undefined;

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  // --- Logic Helpers ---
  const legalEntityCount = data?.legalEntityCount ?? data?.legalEntityIds?.length ?? 0;

  // 3. Refactor handleContextMenu to use kernel.current for all dynamic data
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    const k = kernel.current;
    if (!k) return;

    const userCtx = k.getUserContext();
    const currentData = k.getInventory().Brand.registry[entityId];
    const currentLang = k.getLanguage();
    const localT = translations[currentLang];

    if (!userCtx.isFaterOrgMember || !currentData) return;
    e.preventDefault();

    const primaryLegalEntity = currentData.primaryLegalEntityId 
      ? k.getInventory().LegalEntity.registry[currentData.primaryLegalEntityId] 
      : null;
    
    const siren = primaryLegalEntity?.registrationNumber;

    const actions = [
      {
        label: localT.editName,
        icon: <Edit size={14} />,
        onClick: () => {
            const current = currentData.name || '';
            const newName = window.prompt(localT.enterBrandName, current);
            if (newName !== null && newName.trim() !== '') {
                k.updateEntity('Brand', entityId, { name: newName.trim() });
            }
        }
      },
      {
        label: localT.editDescription,
        icon: <FileText size={14} />,
        onClick: () => {
            const current = currentData.description || '';
            const newDesc = window.prompt(localT.enterBrandDescription, current);
            if (newDesc !== null) {
                k.updateEntity('Brand', entityId, { description: newDesc.trim() || null });
            }
        }
      },
      {
        label: localT.editWebsite,
        icon: <MemoizedGlobe size={14} />,
        onClick: () => {
            const current = currentData.websiteUrl || '';
            const newUrl = window.prompt(localT.enterWebsite, current);
            if (newUrl !== null) {
                k.updateEntity('Brand', entityId, { websiteUrl: newUrl.trim() || null });
            }
        }
      },
      {
        label: localT.editSiren,
        icon: <Fingerprint size={14} />,
        onClick: () => {
            const current = siren || '';
            const newSiren = window.prompt(localT.enterSiren, current);
            if (newSiren !== null) {
                k.updateEntity('Brand', entityId, { siren: newSiren.trim() || null } as any);
            }
        }
      }
    ];

    if (userCtx.isFaterAdmin) {
      // The Fater stamp is set by hand, here (owner, 2026-10-02): first in the menu, it is the
      // decision this list exists for.
      const stamped = currentData.qualification?.stamp?.recommended === true;
      actions.unshift({
        label: stamped ? localT.removeFaterStamp : localT.addFaterStamp,
        icon: <FaterStampIcon size={14} />,
        onClick: () => {
          k.setBrandFaterStamp(entityId, !stamped)
            .then(() => toast.success(stamped ? localT.stampRemoved : localT.stampAdded))
            .catch((error: Error) => toast.error(error.message));
        },
      });
    }

    if (currentData.logoUrl) {
      actions.push({
        label: localT.deleteLogo,
        icon: <XCircle size={14} className="text-white/40" />,
        onClick: () => k.updateEntity('Brand', entityId, { logoUrl: null })
      });
    }

    actions.push({
      label: localT.deleteBrand,
      icon: <Trash2 size={14} className="text-white/40" />,
      onClick: async () => {
        if (window.confirm(localT.deleteConfirm)) {
          await k.deleteEntity('Brand', entityId);
        }
      }
    });

    k.setMenuData({ position: { x: e.clientX, y: e.clientY }, actions });
  }, [entityId, kernel]);

  // Show the precise CNCC leaf the leasing teams share (e.g. "Chaussures mixtes") rather than the
  // coarse internal enum ("Fashion and apparel"). Fall back to the famille, then the Fater category.
  const formattedCategory = useMemo(
    () =>
      data?.cnccActivity?.trim() ||
      data?.cnccSubFamily?.trim() ||
      formatCnccSector(data?.cnccSector) ||
      formatSentenceCase(data?.category),
    [data?.cnccActivity, data?.cnccSubFamily, data?.cnccSector, data?.category]
  );
  const observedLocationsCount = useMemo(() => {
    if (data?.qualification?.stamp?.dataReady) {
      const count = data.qualification.stamp.frenchStores;
      return typeof count === 'number' && Number.isInteger(count) && count >= 0 ? count : null;
    }
    if (typeof data?.locationCount !== 'number' || !Number.isFinite(data.locationCount)) return null;
    return Math.max(0, Math.round(data.locationCount));
  }, [data?.locationCount, data?.qualification?.stamp?.dataReady, data?.qualification?.stamp?.frenchStores]);
  const estimatedLocationsCount = useMemo(() => {
    if (typeof data?.declaredNetworkSize !== 'number' || !Number.isFinite(data.declaredNetworkSize)) return null;
    return Math.max(0, Math.round(data.declaredNetworkSize));
  }, [data?.declaredNetworkSize]);
  const networkCountDisplay = useMemo(
    () => ({
      observed: observedLocationsCount ?? '-',
      estimated: estimatedLocationsCount ?? '?',
    }),
    [estimatedLocationsCount, observedLocationsCount]
  );
  const brandDisplayName = useMemo(() => {
    const normalized = typeof data?.name === 'string' ? data.name.trim() : '';
    return normalized.length > 0 ? normalized : 'Unnamed brand';
  }, [data?.name]);
  const brandInitials = useMemo(() => {
    const normalized = typeof data?.name === 'string' ? data.name.trim() : '';
    if (!normalized) return '??';
    const words = normalized.split(/\s+/).filter((word) => word.length > 0);
    if (words.length >= 2) {
      return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase();
    }
    return normalized.slice(0, 2).toUpperCase();
  }, [data?.name]);
  const brandCountryCodes = useMemo(() => {
    const codes = new Set<string>();
    if (Array.isArray(data?.operatingCountryCodes)) {
      data.operatingCountryCodes.forEach((rawCode) => {
        const normalized = normalizeIsoAlpha2CountryCode(rawCode);
        if (normalized) codes.add(normalized);
      });
    }
    if (Array.isArray(data?.locations)) {
      data.locations.forEach((location) => {
        const normalized = normalizeIsoAlpha2CountryCode(location.country);
        if (normalized) codes.add(normalized);
      });
    }
    return Array.from(codes).sort();
  }, [data?.locations, data?.operatingCountryCodes]);
  const visibleBrandCountryCodes = useMemo(() => brandCountryCodes.slice(0, 3), [brandCountryCodes]);
  const hiddenBrandCountryCount = Math.max(0, brandCountryCodes.length - visibleBrandCountryCodes.length);
  // How the brand positions itself, one sentence (owner, 2026-10-14: the four tiers misled); '' = unknown.
  const positioningNote = data?.positioningNote?.trim() || null;

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- pre-existing switch; the default branch is the intended catch-all
  switch (variant) {
    case 'chip':
      return (
        <div
          className={clsx(
            "flex items-center justify-center shrink-0 cursor-pointer",
            "h-[38px] w-[38px]",
            className
          )}
          onClick={handleClick}
          title={brandDisplayName}
        >
          {data?.logoUrl ? (
            <BrandMark logoUrl={data.logoUrl} name={brandDisplayName} size={30} radius={6} />
          ) : (
            <span className="text-xs font-bold text-white/20">
              {brandInitials}
            </span>
          )}
        </div>
      );

    case 'row':
      // eslint-disable-next-line no-case-declarations -- pre-existing case-scoped declaration
      const cellRenderers: Record<string, React.ReactNode> = {
        // ChatGPT's table (owner, 2026-09-30: 'the table UI, icons, score is very bad'): words and
        // figures at 13px, the value in white and its context in grey, no pills, no decorative
        // icons. A stamped brand shows the seal alone (owner: 'just put the stamp, it's sufficient').
        qualification: !data.qualification?.stamp ? <span className={ROW_MUTED} style={ROW_TEXT}>-</span>
          : data.qualification.stamp.recommended ? (
            <span className="inline-flex items-center" role="img" aria-label={lang === 'fr' ? 'Tampon Fater' : 'Fater stamp'} title={lang === 'fr' ? 'Tampon Fater' : 'Fater stamp'}><FaterStampIcon size={28} /></span>
          ) : data.qualification.stamp.privileged && data.qualification.stamp.dataGaps?.length ? (
            // A brand still enriching says what it lacks, so the work to do is read off the list.
            <AppTooltip contentClassName="z-[120] block max-w-xs p-3 text-left" content={(
              <ul className="space-y-1">
                {data.qualification.stamp.dataGaps.map((gap) => <li key={gap}>{GAP_LABEL[gap]?.[lang === 'fr' ? 1 : 0] ?? gap}</li>)}
              </ul>
            )}>
              <button type="button" className={clsx(ROW_MUTED, 'whitespace-nowrap')} style={ROW_TEXT}
                onClick={(event) => event.stopPropagation()}
                aria-label={(lang === 'fr' ? 'Manque : ' : 'Missing: ') + data.qualification.stamp.dataGaps.map((gap) => GAP_LABEL[gap]?.[lang === 'fr' ? 1 : 0] ?? gap).join(', ')}>
                {lang === 'fr' ? 'Manque' : 'Missing'}: {data.qualification.stamp.dataGaps.length}
              </button>
            </AppTooltip>
          ) : <span className={ROW_MUTED} style={ROW_TEXT}>{data.qualification.stamp.dataReady ? (lang === 'fr' ? 'Prête' : 'Ready') : data.qualification.stamp.eligible ? (lang === 'fr' ? 'Notée' : 'Scored') : (lang === 'fr' ? 'Hors marché' : 'Out of market')}</span>,
        internalPriority: data.qualification?.stamp?.eligible
          ? <span className={clsx(ROW_VALUE, 'font-medium tabular-nums')} style={ROW_TEXT}>{Math.round(data.qualification.stamp.score)}</span>
          : <span className={ROW_MUTED} style={ROW_TEXT}>-</span>,
        // Fater's read of how hot the brand is; the reason rides in the tooltip.
        aura: (() => {
          const aura = data.qualification?.stamp?.components.aura;
          if (!aura?.known) return <span className={ROW_MUTED} style={ROW_TEXT}>-</span>;
          const level = aura.reason.split(':')[0]!;
          const fr = { Hot: 'En vogue', Rising: 'Montante', Desirable: 'Désirable', Neutral: 'Neutre', Fading: 'Déclinante' }[level];
          return <span className={aura.points >= 16 ? ROW_VALUE : ROW_MUTED} style={ROW_TEXT} title={aura.reason}>{lang === 'fr' && fr ? fr : level}</span>;
        })(),
        evidenceCoverage: <span className={clsx(ROW_MUTED, 'tabular-nums')} style={ROW_TEXT}>{data.qualification?.stamp ? `${data.qualification.stamp.coverage}%` : '-'}</span>,
        brand: (
          <div className="h-full min-w-0 flex items-center gap-3">
            <div className="h-8 w-8 flex items-center justify-center shrink-0">
              {data.logoUrl ? (
                <BrandMark logoUrl={data.logoUrl} name={brandDisplayName} size={28} radius={8} />
              ) : (
                <span className="text-xs font-bold text-white/20">{brandInitials}</span>
              )}
            </div>
            <div className="min-w-0">
              <div className="truncate text-[#ECECEC]" style={NAME_TEXT}>{brandDisplayName}</div>
              <div className="truncate text-[#8E8E8E]" style={ROW_TEXT}>{formattedCategory}</div>
            </div>
          </div>
        ),
        data: visibleBrandCountryCodes.length > 0 ? (
          // Codes, not flags: a row of emoji flags was the loudest thing on a grey table.
          <span className="whitespace-nowrap tabular-nums" title={brandCountryCodes.join(', ')} data-cell-text={brandCountryCodes.join(', ')} style={ROW_TEXT}>
            <span className={ROW_VALUE} style={ROW_TEXT}>{visibleBrandCountryCodes.join(', ')}</span>
            {hiddenBrandCountryCount > 0 && <span className={ROW_MUTED} style={ROW_TEXT}> +{hiddenBrandCountryCount}</span>}
          </span>
        ) : <span className={ROW_MUTED} style={ROW_TEXT}>-</span>,
        typology: <span className={ROW_MUTED} style={ROW_TEXT}>{data.conceptFormats?.[0] ? formatSentenceCase(data.conceptFormats[0]) : '-'}</span>,
        positioning: <span className={clsx(positioningNote ? ROW_VALUE : ROW_MUTED, 'block truncate')} title={positioningNote ?? undefined} style={ROW_TEXT}>{positioningNote ?? '-'}</span>,
        surface: <span className={clsx(ROW_VALUE, 'tabular-nums')} style={ROW_TEXT}>{data.surfaceStandard ? `${data.surfaceStandard} m²` : '-'}</span>,
        network: (
          <span className="tabular-nums whitespace-nowrap" title={data.qualification?.stamp?.dataReady
            ? (lang === 'fr' ? 'Magasins français actifs vérifiés' : 'Verified active French shops')
            : (lang === 'fr' ? 'Magasins trouvés / taille de réseau déclarée' : 'Stores found / network size the brand declares')} style={ROW_TEXT}>
            <span className={ROW_VALUE} style={ROW_TEXT}>{networkCountDisplay.observed}</span>
            <span className={ROW_MUTED} style={ROW_TEXT}>{data.qualification?.stamp?.dataReady ? (observedLocationsCount === null ? '' : ' FR') : ` / ${networkCountDisplay.estimated}`}</span>
          </span>
        ),
        spacing: <span className={clsx(!data.qualification?.stamp?.dataReady && data.annd ? ROW_VALUE : ROW_MUTED, 'tabular-nums')} style={ROW_TEXT}>{!data.qualification?.stamp?.dataReady && data.annd ? `${data.annd.toFixed(1)} km` : '-'}</span>,
        actions: (
          <div className="h-full flex items-center justify-end">
            <EntityRowActions entityType="Brand" entity={data} rowActions={rowActions} />
          </div>
        ),
      };

      // eslint-disable-next-line no-case-declarations -- pre-existing case-scoped declaration
      const activeColumns: TableColumnDef[] = columns ?? DEFAULT_ROW_COLUMNS;

      return (
        <div
          className={clsx(
            portalTableRow,
            styles.gridRow,
            styles.bodyRow,
            'group h-14 w-full cursor-pointer border-b border-white/[0.06]',
            'hover:bg-[#1A1A1A]',
            className
          )}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          style={PANEL_STYLE}
        >
          {activeColumns.map((column) => (
            <div
              key={column.id}
              className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
              style={resolveCellStyle(column)}
            >
              {cellRenderers[column.id] || null}
            </div>
          ))}
        </div>
      );
    case 'card':
      return (
        <div
          className={clsx(
            "flex overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:bg-white/10 hover:border-white/20 cursor-pointer relative group",
            "flex-col md:flex-row w-full",
            className
          )}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          style={PANEL_STYLE}
        >
          {data.logoUrl && (
            <div className={clsx("shrink-0 bg-[#1A1A1A] p-2 flex items-center justify-center border-white/10", "w-full md:w-32 h-24 md:h-auto border-b md:border-b-0 md:border-r")}>
              {/* mix-blend-screen used to live here to rescue dark marks on a dark tile. It drops
                  black to transparent, which also eats the dark detail inside a coloured logo. The
                  tile now follows the mark, so the blend hack is gone. */}
              <BrandMark logoUrl={data.logoUrl} name={brandDisplayName} size={72} radius={10} />
            </div>
          )}
          <div className="p-4 flex flex-col justify-center min-w-0 flex-1">
            <div className="text-[10px] text-white/40 mb-1">{formattedCategory}</div>
            <div className="text-sm font-medium text-white/90 truncate">{brandDisplayName}</div>
            {data.summary && <div className="text-xs text-white/50 mt-2 line-clamp-2">{data.summary}</div>}
            <div className="flex items-center gap-3 mt-3 text-[10px] text-white/40">
              {visibleBrandCountryCodes.length > 0 && (
                <span className="flex items-center gap-1" title={brandCountryCodes.join(', ')}>
                  {visibleBrandCountryCodes.map((countryCode) => (
                    <span key={countryCode} className="text-[11px] leading-none text-white/80">
                      {toCountryFlag(countryCode)}
                    </span>
                  ))}
                  {hiddenBrandCountryCount > 0 && (
                    <span className="text-[9px] text-white/35">+{hiddenBrandCountryCount}</span>
                  )}
                </span>
              )}
              <span
                className="flex items-center gap-1"
                title={data.qualification?.stamp?.dataReady
                  ? (lang === 'fr' ? 'Magasins français actifs vérifiés' : 'Verified active French shops')
                  : 'Observed locations / Estimated network size'}
              >
                <MemoizedMapPin size={10}/>
                <span className="text-white/80">{networkCountDisplay.observed}</span>
                {!data.qualification?.stamp?.dataReady && <span className="text-white/35">/ {networkCountDisplay.estimated}</span>}
                <span>{data.qualification?.stamp?.dataReady ? (observedLocationsCount === null ? '' : 'FR') : t.locs}</span>
              </span>
              <span className="flex items-center gap-1"><MemoizedActivity size={10}/> {data.sortableFinancialScore || '-'}/100</span>
            </div>
          </div>
        </div>
      );
  }
});

BrandRenderer.displayName = 'BrandRenderer';
